import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import { promptLink } from '../../lib/ui-links.js';

interface ExperimentRunOptions {
  evaluation: string;
  dataset: string;
  execModel: string;
  evalModel?: string;
}

interface ExperimentDeleteOptions {
  force?: boolean;
}

export function registerExperimentCommands(prompts: Command): void {
  const experiment = new Command('experiment')
    .description('Manage prompt experiments (multi-model dataset runs)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts experiment run <prompt-id> --evaluation <id> --dataset <id> --exec-model claude-sonnet-4-5-20250929
  ${chalk.dim('$')} mutagent prompts experiment list <prompt-id>
  ${chalk.dim('$')} mutagent prompts experiment get <experiment-id>
  ${chalk.dim('$')} mutagent prompts experiment delete <experiment-id> --force
    `)
    .action(() => {
      experiment.help();
    });
  prompts.addCommand(experiment);

  // Run experiment
  experiment
    .command('run')
    .description('Create and run an experiment across a dataset')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .requiredOption('-e, --evaluation <id>', 'Evaluation ID to score with')
    .requiredOption('-d, --dataset <id>', 'Dataset ID to run against')
    .requiredOption('--exec-model <model>', 'Model to generate responses (e.g. "claude-sonnet-4-5-20250929")')
    .option('--eval-model <model>', 'Model to score responses (defaults to exec-model)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts experiment run <prompt-id> -e <eval-id> -d <dataset-id> --exec-model claude-sonnet-4-5-20250929
  ${chalk.dim('$')} mutagent prompts experiment run <prompt-id> -e <eval-id> -d <dataset-id> --exec-model claude-sonnet-4-5-20250929 --eval-model gpt-4o
  ${chalk.dim('$')} mutagent prompts experiment run <prompt-id> -e <eval-id> -d <dataset-id> --exec-model claude-sonnet-4-5-20250929 --json

${chalk.dim('--exec-model generates output; --eval-model scores it (defaults to exec-model).')}
${chalk.yellow('Tip:')} Use --json for machine-readable output with _links for dashboard navigation.
    `)
    .action(async (promptId: string, options: ExperimentRunOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const evaluationId = parseInt(options.evaluation, 10);
        if (isNaN(evaluationId)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Invalid evaluation ID: "${options.evaluation}" (must be numeric)`,
            'Run: mutagent prompts evaluation list <prompt-id> to find valid IDs'
          );
        }
        const datasetId = parseInt(options.dataset, 10);
        if (isNaN(datasetId)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Invalid dataset ID: "${options.dataset}" (must be numeric)`,
            'Run: mutagent prompts dataset list <prompt-id> to find valid IDs'
          );
        }
        const promptIdNum = parseInt(promptId, 10);
        if (isNaN(promptIdNum)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Invalid prompt ID: "${promptId}" (must be numeric)`,
            'Run: mutagent prompts list to find valid IDs'
          );
        }

        const models = [
          { id: `col_${Date.now()}`, model: options.execModel, temperature: 0 },
          ...(options.evalModel && options.evalModel !== options.execModel
            ? [{ id: `col_${Date.now() + 1}`, model: options.evalModel, temperature: 0 }]
            : []),
        ];

        const client = await getSDKClient();
        const experiment = await client.createExperiment({
          promptId: promptIdNum,
          promptGroupId: promptId,
          evaluationId,
          datasetId,
          models,
        });

        // Try to execute the experiment — 404/501 = coming soon
        try {
          await client.executeExperiment(experiment.experimentId);
        } catch (execError) {
          if (execError instanceof ApiError && (execError.statusCode === 404 || execError.statusCode === 501)) {
            if (!isJson) {
              output.warn('Experiment execution is not yet available server-side. The experiment has been created and will run when the feature is enabled.');
            }
          } else if (execError instanceof Error) {
            // Non-critical — log but don't fail
            if (!isJson) {
              output.warn(`Experiment created but auto-execute failed: ${execError.message}`);
            }
          }
        }

        if (isJson) {
          output.output({
            ...experiment,
            _links: {
              dashboard: promptLink(promptId),
              api: `/api/prompts/experiments/${experiment.experimentId}`,
            },
          });
        } else {
          output.success(`Experiment created: ${experiment.name} (id: ${experiment.experimentId})`);
          output.info(`Status: ${experiment.status}`);
          output.info(`Dashboard: ${promptLink(promptId)}`);
          output.info(`Check progress: mutagent prompts experiment get ${experiment.experimentId}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // List experiments
  experiment
    .command('list')
    .description('List experiments for a prompt')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts experiment list <prompt-id>
  ${chalk.dim('$')} mutagent prompts experiment list <prompt-id> --json
    `)
    .action(async (promptId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const promptIdNum = parseInt(promptId, 10);
        if (isNaN(promptIdNum)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Invalid prompt ID: "${promptId}" (must be numeric)`,
            'Run: mutagent prompts list to find valid IDs'
          );
        }

        const client = await getSDKClient();
        const { experiments, total } = await client.listExperiments({ promptId: promptIdNum });

        if (isJson) {
          output.output({
            experiments,
            total,
            _links: {
              dashboard: promptLink(promptId),
              api: `/api/prompts/experiments?promptId=${promptId}`,
            },
          });
        } else {
          if (experiments.length === 0) {
            output.info(`No experiments found for prompt ${promptId}`);
            output.info(`Create one: mutagent prompts experiment run ${promptId} --evaluation <id> --dataset <id> --exec-model <model>`);
          } else {
            output.info(`Found ${String(total)} experiment(s) for prompt ${promptId}:`);
            output.output(experiments);
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get experiment
  experiment
    .command('get')
    .description('Get details of a specific experiment')
    .argument('<experiment-id>', 'Experiment ID (from: mutagent prompts experiment list <prompt-id>)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts experiment get <experiment-id>
  ${chalk.dim('$')} mutagent prompts experiment get <experiment-id> --json
    `)
    .action(async (experimentId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const exp = await client.getExperiment(experimentId);

        if (isJson) {
          output.output({
            ...exp,
            _links: {
              api: `/api/prompts/experiments/${experimentId}`,
            },
          });
        } else {
          const name = typeof exp.name === 'string' ? exp.name : experimentId;
          const status = typeof exp.status === 'string' ? exp.status : 'unknown';
          output.success(`Experiment: ${name} (id: ${experimentId})`);
          output.info(`Status: ${status}`);
          if (exp.promptId !== undefined) output.info(`Prompt ID: ${String(exp.promptId)}`);
          if (exp.createdAt !== undefined) output.info(`Created: ${new Date(String(exp.createdAt)).toLocaleString()}`);
        }
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
          handleError(
            new MutagentError(
              'NOT_FOUND',
              `Experiment ${experimentId} not found`,
              'Run: mutagent prompts experiment list <prompt-id> to see available experiments'
            ),
            isJson,
          );
        } else {
          handleError(error, isJson);
        }
      }
    });

  // Delete experiment
  experiment
    .command('delete')
    .description('Delete an experiment')
    .argument('<experiment-id>', 'Experiment ID')
    .option('--force', 'Skip confirmation prompt')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts experiment delete <experiment-id> --force
  ${chalk.dim('$')} mutagent prompts experiment delete <experiment-id> --json
  ${chalk.dim('$')} mutagent prompts experiment delete <experiment-id> --force --json

${chalk.red('Warning:')} Deletion is permanent. Use --force or --json to confirm.
    `)
    .action(async (experimentId: string, options: ExperimentDeleteOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Deleting experiment ${experimentId} requires confirmation`,
            'Run: mutagent prompts experiment delete --help\n' +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent prompts experiment delete ${experimentId} --force`
          );
        }

        const client = await getSDKClient();
        try {
          await client.deleteExperiment(experimentId);
        } catch (deleteError) {
          if (deleteError instanceof ApiError && deleteError.statusCode === 404) {
            if (isJson) {
              output.output({ success: true, deletedId: experimentId, _links: { api: '/api/prompts/experiments' } });
            } else {
              output.success(`Experiment ${experimentId} already deleted (idempotent)`);
            }
            return;
          }
          throw deleteError;
        }

        if (isJson) {
          output.output({
            success: true,
            deletedId: experimentId,
            _links: { api: '/api/prompts/experiments' },
          });
        } else {
          output.success(`Experiment ${experimentId} deleted successfully`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
